"use client";

import {
  COOKIE_NAME_SELECTED_WORKSPACE_ID,
  getSelectedWorkspaceId,
} from "../workspace/utils";
import type { GetOrganizationsRoute } from "@/app/api/organizations/route";
import { useEffectOnlyOnChange } from "@/utils/hooks";
import { getJson } from "@/utils/routeUtils";
import type { OrganizationId } from "@/utils/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useCookies } from "react-cookie";

export type SessionWorkspace = NonNullable<
  GetOrganizationsRoute["output"]
>[number];

type WorkspaceProviderContext = {
  setOrgId: (workspaceId: OrganizationId) => void;
  orgs: SessionWorkspace[] | null;
  org: SessionWorkspace | null;
  refreshOrgs: () => Promise<void>;
  setDeeplinkOrgId: (orgId: OrganizationId | null) => void;
};

const Context = createContext<WorkspaceProviderContext | undefined>(undefined);

export default function WorkspaceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [cookies, setCookie] = useCookies([COOKIE_NAME_SELECTED_WORKSPACE_ID]);
  const cookieWorkspaceId = cookies[COOKIE_NAME_SELECTED_WORKSPACE_ID] as
    | OrganizationId
    | undefined;

  // `orgId` state is the source of truth; seeded from the cookie (SSR hint) and
  // overridden by localStorage (durable) on the client.
  const [workspaceId, setWorkspaceId] = useState<OrganizationId | null>(
    cookieWorkspaceId ?? null
  );
  const [org, setWorkspace] = useState<SessionWorkspace | null>(null);
  const [deeplinkWorkspaceId, setDeeplinkWorkspaceId] =
    useState<OrganizationId | null>(null);

  const { data: workspaces, isFetching: isWorkspacesFetching } = useQuery({
    queryKey: ["orgs"],
    queryFn: async (): Promise<SessionWorkspace[]> => {
      const { data, error } = await getJson("/api/organizations", {});
      if (error || !data) {
        throw new Error(error ?? "Failed to fetch workspaces");
      }
      return data;
    },
  });
  const queryClient = useQueryClient();
  const router = useRouter();

  // Cookie = SSR hint; writing it doesn't broadcast cross-tab (localStorage does).
  const writeCookie = useCallback(
    (id: OrganizationId) => {
      setCookie(COOKIE_NAME_SELECTED_WORKSPACE_ID, id, {
        sameSite: "strict",
        // `secure` cookies are rejected over http (local dev); only set on https.
        secure: window.location.protocol === "https:",
        path: "/",
        maxAge: 60 * 60 * 24 * 400, // 400 days, max recommended age
      });
    },
    [setCookie]
  );

  const readStoredWorkspaceId = useCallback((): OrganizationId | null => {
    try {
      return localStorage.getItem(COOKIE_NAME_SELECTED_WORKSPACE_ID);
    } catch {
      return null;
    }
  }, []);

  // Reads the cookie straight from `document.cookie` (react-cookie state lags
  // within the same render), to confirm a write actually landed.
  const readCookieWorkspaceId = useCallback((): string | undefined => {
    if (typeof document === "undefined") {
      return undefined;
    }
    return document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${COOKIE_NAME_SELECTED_WORKSPACE_ID}=`))
      ?.split("=")[1];
  }, []);

  const replaceUrlParams = useCallback(
    (params: URLSearchParams) => {
      const path = window.location.pathname;
      const query = params.toString();
      router.replace(
        `${path}${query ? `?${query}` : ""}${window.location.hash}`,
        {
          scroll: false,
        }
      );
    },
    [router]
  );

  const stripUrlParams = useCallback(
    (keys: string[]) => {
      const params = new URLSearchParams(window.location.search);
      if (!keys.some((key) => params.has(key))) {
        return;
      }
      keys.forEach((key) => params.delete(key));
      replaceUrlParams(params);
    },
    [replaceUrlParams]
  );

  // `__org_id` is the SSR override, needed only when the cookie can't carry the
  // selection. Set it (write failed) or strip it (cookie holds), no-op if the
  // URL already matches.
  const syncWorkspaceParam = useCallback(
    (id: OrganizationId | null) => {
      const params = new URLSearchParams(window.location.search);
      if ((params.get("__org_id") ?? null) === id) {
        return;
      }
      if (id) {
        params.set("__org_id", id);
      } else {
        params.delete("__org_id");
      }
      replaceUrlParams(params);
    },
    [replaceUrlParams]
  );

  // Cross-tab handlers use this path to avoid echoing storage events.
  const adoptWorkspace = useCallback(
    (newWorkspaceId: OrganizationId) => {
      setWorkspaceId(newWorkspaceId);
      setDeeplinkWorkspaceId(null);
      writeCookie(newWorkspaceId);
      syncWorkspaceParam(
        readCookieWorkspaceId() === newWorkspaceId ? null : newWorkspaceId
      );
    },
    [writeCookie, readCookieWorkspaceId, syncWorkspaceParam]
  );

  // Explicit selection: state + localStorage (durable + cross-tab signal) +
  // cookie. Clears any pending deeplink. Keeps `__org_id` as the SSR override
  // only when the cookie write was rejected, so the server can still follow.
  const selectWorkspace = useCallback(
    (newWorkspaceId: OrganizationId) => {
      adoptWorkspace(newWorkspaceId);
      try {
        localStorage.setItem(COOKIE_NAME_SELECTED_WORKSPACE_ID, newWorkspaceId);
      } catch {
        // localStorage may be unavailable (private mode / quota).
      }
    },
    [adoptWorkspace]
  );

  // Resolve the active org by priority: deeplink, current selection, durable
  // localStorage, then a computed default. An org not in our list yet (freshly
  // created / picked in another tab) triggers one refetch and waits.
  const refetchedForWorkspaceIdRef = useRef<OrganizationId | null>(null);
  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (!workspaces) {
      return;
    }
    let currentWorkspaceId = workspaceId;
    if (!didHydrateRef.current) {
      didHydrateRef.current = true;
      const hasDeeplink = new URLSearchParams(window.location.search).has(
        "__org_id"
      );
      const stored = hasDeeplink ? null : readStoredWorkspaceId();
      if (stored && stored !== currentWorkspaceId) {
        // Restore the durable selection; selectOrg also resyncs the cookie and
        // the `__org_id` SSR override so a stale cookie doesn't keep diverging.
        currentWorkspaceId = stored;
        selectWorkspace(stored);
      }
    }

    const desiredWorkspaceId = deeplinkWorkspaceId ?? currentWorkspaceId;
    if (
      desiredWorkspaceId &&
      workspaces.some((o) => o.id === desiredWorkspaceId)
    ) {
      // Commit a deeplink as an explicit selection; `selectOrg` also manages
      // `__org_id` (kept only if the cookie write was rejected).
      if (deeplinkWorkspaceId) {
        selectWorkspace(deeplinkWorkspaceId);
      }
      refetchedForWorkspaceIdRef.current = null;
      return;
    }
    if (desiredWorkspaceId) {
      if (isWorkspacesFetching) {
        return;
      }
      if (refetchedForWorkspaceIdRef.current !== desiredWorkspaceId) {
        refetchedForWorkspaceIdRef.current = desiredWorkspaceId;
        void queryClient.invalidateQueries({ queryKey: ["orgs"] });
        return;
      }
      // A completed refetch still lacks it: it's inaccessible. Drop the stale
      // deeplink, URL override, and durable selection so reloads stop
      // re-hydrating it.
      if (deeplinkWorkspaceId) {
        setDeeplinkWorkspaceId(null);
      }
      stripUrlParams(["__org_id"]);
      if (readStoredWorkspaceId() === desiredWorkspaceId) {
        try {
          localStorage.removeItem(COOKIE_NAME_SELECTED_WORKSPACE_ID);
        } catch {
          // ignore unavailable localStorage
        }
      }
    }
    // Default: persist to the cookie only (no localStorage/broadcast) so SSR
    // agrees next render; client/server list orderings can otherwise differ.
    const targetWorkspaceId = getSelectedWorkspaceId({
      savedOrgId: cookieWorkspaceId,
      deeplinkOrgId: deeplinkWorkspaceId ?? undefined,
      orgs: workspaces,
    });
    if (targetWorkspaceId) {
      setWorkspaceId(targetWorkspaceId);
      writeCookie(targetWorkspaceId);
    }
  }, [
    deeplinkWorkspaceId,
    workspaces,
    workspaceId,
    isWorkspacesFetching,
    cookieWorkspaceId,
    selectWorkspace,
    writeCookie,
    queryClient,
    readStoredWorkspaceId,
    stripUrlParams,
  ]);

  useEffectOnlyOnChange(() => {
    if (workspaces) {
      setWorkspace(workspaces.find((o) => o.id === workspaceId) ?? null);
    }
  }, [workspaceId, workspaces]);

  // Cross-tab sync: follow explicit selections without rebroadcasting them.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === COOKIE_NAME_SELECTED_WORKSPACE_ID && e.newValue) {
        // Queued event payloads can be older than the current stored value.
        const currentStoredWorkspaceId = readStoredWorkspaceId();
        if (currentStoredWorkspaceId) {
          adoptWorkspace(currentStoredWorkspaceId);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [adoptWorkspace, readStoredWorkspaceId]);

  // Workspace-keyed queries switch automatically; do not invalidate unrelated data.
  const prevWorkspaceIdRef = useRef(org?.id);
  useEffect(() => {
    if (
      prevWorkspaceIdRef.current &&
      org?.id &&
      prevWorkspaceIdRef.current !== org?.id
    ) {
      // `__org_id` is owned by selectOrg/syncOrgParam; only clear folder_id here.
      stripUrlParams(["folder_id"]);
    }
    prevWorkspaceIdRef.current = org?.id;
  }, [org?.id, stripUrlParams]);

  return (
    <Context.Provider
      value={{
        setOrgId: selectWorkspace,
        orgs: workspaces ?? null,
        org,
        setDeeplinkOrgId: setDeeplinkWorkspaceId,
        refreshOrgs: () =>
          queryClient.invalidateQueries({ queryKey: ["orgs"] }),
      }}
    >
      {children}
    </Context.Provider>
  );
}

/** Undefined outside WorkspaceProvider, e.g. on shared pages without a signed-in session. */
export const useOptionalSessionWorkspace = () => useContext(Context);

export const useSessionWorkspace = () => {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error("useOrgProvider must be used inside OrgProvider");
  }
  return context;
};
